import CryptoJS from 'crypto-js';
import JSEncrypt from 'jsencrypt';

/**
 * Private Chat Encryption Utilities
 * Handles RSA and AES encryption for private chat sessions
 */

// Generate a random AES key (32 bytes = 256 bits)
export const generateAESKey = () => {
  return CryptoJS.lib.WordArray.random(32).toString();
};

// Generate RSA key pair for session
export const generateSessionRSAKeyPair = () => {
  try {
    const encrypt = new JSEncrypt({default_key_size: 2048});
    const publicKey = encrypt.getPublicKey();
    const privateKey = encrypt.getPrivateKey();

    return {
      publicKey: publicKey,
      privateKey: privateKey
    };
  } catch (error) {
    throw new Error('Failed to generate session keys');
  }
};

// Encrypt data with AES
export const encryptWithAES = (data, aesKey) => {
  try {
    const encrypted = CryptoJS.AES.encrypt(data, aesKey).toString();
    return encrypted;
  } catch (error) {
    throw new Error('AES encryption failed');
  }
};

// Decrypt data with AES
export const decryptWithAES = (encryptedData, aesKey) => {
  try {
    const decrypted = CryptoJS.AES.decrypt(encryptedData, aesKey);
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    throw new Error('AES decryption failed');
  }
};

// Encrypt data with RSA public key (PEM format)
export const encryptWithRSA = (data, publicKeyPEM) => {
  try {
    const encrypt = new JSEncrypt();
    encrypt.setPublicKey(publicKeyPEM);
    const encrypted = encrypt.encrypt(data);
    
    if (!encrypted) {
      throw new Error('RSA encryption failed - invalid public key or data');
    }
    
    return encrypted;
  } catch (error) {
    throw new Error('RSA encryption failed');
  }
};

// Decrypt data with RSA private key (PEM format)
export const decryptWithRSA = (encryptedData, privateKeyPEM) => {
  try {
    const decrypt = new JSEncrypt();
    decrypt.setPrivateKey(privateKeyPEM);
    const decrypted = decrypt.decrypt(encryptedData);
    
    if (!decrypted) {
      throw new Error('RSA decryption failed - invalid private key or data');
    }
    
    return decrypted;
  } catch (error) {
    throw new Error('RSA decryption failed');
  }
};

// Encrypt session private key with AES
export const encryptSessionPrivateKey = (privateKey, aesKey) => {
  return encryptWithAES(privateKey, aesKey);
};

// Decrypt session private key with AES
export const decryptSessionPrivateKey = (encryptedPrivateKey, aesKey) => {
  return decryptWithAES(encryptedPrivateKey, aesKey);
};

// Encrypt AES key with user's RSA public key
export const encryptAESKeyWithUserPublicKey = (aesKey, userPublicKeyPEM) => {
  return encryptWithRSA(aesKey, userPublicKeyPEM);
};

// Decrypt AES key with user's RSA private key
export const decryptAESKeyWithUserPrivateKey = (encryptedAESKey, userPrivateKeyPEM) => {
  return decryptWithRSA(encryptedAESKey, userPrivateKeyPEM);
};

// Encrypt message for recipient (using recipient's session RSA public key)
export const encryptMessageForRecipient = (message, recipientPublicKeyPEM) => {
  return encryptWithRSA(message, recipientPublicKeyPEM);
};

// Encrypt message for sender (using sender's session RSA public key)
export const encryptMessageForSender = (message, senderPublicKeyPEM) => {
  return encryptWithRSA(message, senderPublicKeyPEM);
};

// Decrypt message (using session RSA private key)
export const decryptMessage = (encryptedMessage, sessionPrivateKeyPEM) => {
  return decryptWithRSA(encryptedMessage, sessionPrivateKeyPEM);
};

// Generate message ID
export const generateMessageID = () => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Validate message content
export const validateMessageContent = (content) => {
  if (!content || typeof content !== 'string') {
    return { valid: false, error: 'Message content is required' };
  }
  
  if (content.trim().length === 0) {
    return { valid: false, error: 'Message content cannot be empty' };
  }
  
  if (content.length > 175) {
    return { valid: false, error: 'Message content cannot exceed 175 characters' };
  }
  
  return { valid: true };
};

// Format timestamp for display
export const formatMessageTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

// Check if message is from current user
export const isMessageFromCurrentUser = (message, currentUserID) => {
  return message.sender_id === currentUserID;
};

// Get message content for display (decrypt based on sender)
export const getMessageContentForDisplay = async (message, currentUserID, sessionPrivateKeyBase64) => {
  try {
    let encryptedContent;
    
    if (isMessageFromCurrentUser(message, currentUserID)) {
      // Use sender's encrypted content (backup)
      encryptedContent = message.content_encrypted_for_sender;
    } else {
      // Use recipient's encrypted content (main)
      encryptedContent = message.content_encrypted_for_recipient;
    }
    
    const decryptedContent = await decryptMessage(encryptedContent, sessionPrivateKeyBase64);
    return decryptedContent;
  } catch (error) {
    return '[Failed to decrypt message]';
  }
};

// Create session encryption setup
export const createSessionEncryptionSetup = (userPublicKeyPEM) => {
  try {
    // Generate session RSA key pair
    const sessionKeys = generateSessionRSAKeyPair();
    
    // Generate AES key
    const aesKey = generateAESKey();
    
    // Encrypt session private key with AES
    const encryptedSessionPrivateKey = encryptSessionPrivateKey(sessionKeys.privateKey, aesKey);
    
    // Encrypt AES key with user's public key
    const encryptedAESKey = encryptAESKeyWithUserPublicKey(aesKey, userPublicKeyPEM);
    
    return {
      sessionRSAPublicKey: sessionKeys.publicKey,
      sessionRSAPrivateKeyEncrypted: encryptedSessionPrivateKey,
      aesKeyEncrypted: encryptedAESKey,
      sessionPrivateKey: sessionKeys.privateKey, // Keep in memory for immediate use
      aesKey: aesKey // Keep in memory for immediate use
    };
  } catch (error) {
    throw new Error('Failed to setup session encryption');
  }
};

// Decrypt session keys for use
export const decryptSessionKeys = (encryptedSessionPrivateKey, encryptedAESKey, userPrivateKeyPEM) => {
  try {
    // Decrypt AES key with user's private key
    const aesKey = decryptAESKeyWithUserPrivateKey(encryptedAESKey, userPrivateKeyPEM);
    
    // Decrypt session private key with AES key
    const sessionPrivateKey = decryptSessionPrivateKey(encryptedSessionPrivateKey, aesKey);
    
    return {
      sessionPrivateKey,
      aesKey
    };
  } catch (error) {
    throw new Error('Failed to decrypt session keys');
  }
};
